import { getSubmissionHeatmap } from '../services/leetcodeAnalyticsService';

// ── Error types ───────────────────────────────────────────────────────────────

/** Thrown when the caller provides invalid arguments. Maps to HTTP 400. */
export class ValidationError extends Error {
  constructor(message) {
    super(message);
    this.name  = 'ValidationError';
    this.status = 400;
  }
}

/** Thrown when the requested resource does not exist. Maps to HTTP 404. */
export class NotFoundError extends Error {
  constructor(message) {
    super(message);
    this.name  = 'NotFoundError';
    this.status = 404;
  }
}

/** Thrown for any unexpected server-side failure. Maps to HTTP 500. */
export class ServerError extends Error {
  constructor(message, cause) {
    super(message);
    this.name  = 'ServerError';
    this.status = 500;
    this.cause  = cause;
  }
}

// ── Controller ────────────────────────────────────────────────────────────────

/**
 * Fetches a GitHub-style submission activity heatmap for a single student.
 *
 * Acts as the controller layer between UI components and the analytics
 * service: validates inputs, delegates to the service, normalises the
 * response shape, and maps errors to typed controller errors.
 *
 * @param {string} userId    - User._id or StudentProfile._id
 * @param {string} startDate - Start of the range, format 'YYYY-MM-DD'
 * @param {string} endDate   - End   of the range, format 'YYYY-MM-DD'
 *
 * @returns {Promise<{
 *   userId:    string,
 *   startDate: string,
 *   endDate:   string,
 *   heatmap:   Array<{ date: string, count: number }>
 * }>}
 *
 * @throws {ValidationError} Invalid / missing arguments or range violation.
 * @throws {NotFoundError}   Student profile not found on the server.
 * @throws {ServerError}     Any other unexpected failure.
 *
 * @example
 * import { getHeatmap } from '../controllers/leetcodeAnalyticsController';
 *
 * const result = await getHeatmap('abc123', '2026-01-01', '2026-03-14');
 * // {
 * //   userId:    'abc123',
 * //   startDate: '2026-01-01',
 * //   endDate:   '2026-03-14',
 * //   heatmap:   [{ date: '2026-01-01', count: 3 }, ...]
 * // }
 */
export const getHeatmap = async (userId, startDate, endDate) => {
  // ── 1. Input validation ──────────────────────────────────────────────────
  if (!userId || typeof userId !== 'string' || !userId.trim()) {
    throw new ValidationError('userId is required and must be a non-empty string.');
  }
  if (!startDate) throw new ValidationError('startDate is required.');
  if (!endDate)   throw new ValidationError('endDate is required.');

  const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;
  if (!DATE_RE.test(startDate)) throw new ValidationError('startDate must be in YYYY-MM-DD format.');
  if (!DATE_RE.test(endDate))   throw new ValidationError('endDate must be in YYYY-MM-DD format.');

  const startMs = Date.parse(startDate);
  const endMs   = Date.parse(endDate);

  if (isNaN(startMs)) throw new ValidationError('startDate is not a valid calendar date.');
  if (isNaN(endMs))   throw new ValidationError('endDate is not a valid calendar date.');

  if (startMs > endMs) {
    throw new ValidationError('startDate must be on or before endDate.');
  }

  const MS_PER_DAY = 24 * 60 * 60 * 1000;
  if ((endMs - startMs) / MS_PER_DAY > 365) {
    throw new ValidationError('Date range must not exceed 365 days.');
  }

  // ── 2. Delegate to service ───────────────────────────────────────────────
  try {
    const heatmap = await getSubmissionHeatmap(userId, startDate, endDate);

    // ── 3. Return normalised response ──────────────────────────────────────
    return {
      userId,
      startDate,
      endDate,
      heatmap, // Array<{ date: string, count: number }>
    };
  } catch (err) {
    // Re-throw validation errors surfaced by the service layer as-is
    if (err.name === 'ValidationError') throw err;

    // Map HTTP 404 from Axios to a NotFoundError
    const httpStatus = err.response?.status;
    if (httpStatus === 404) {
      throw new NotFoundError(
        err.response?.data?.message || 'Student profile not found.'
      );
    }

    // Map HTTP 400 from Axios to a ValidationError
    if (httpStatus === 400) {
      throw new ValidationError(
        err.response?.data?.message || 'Invalid request parameters.'
      );
    }

    // Everything else → ServerError
    throw new ServerError(
      err.response?.data?.message || 'Failed to load heatmap data.',
      err
    );
  }
};
